import { formatTimeSl, timeToMinutes, type SleepTimes } from '@planer/shared';
import { Moon, Sun } from 'lucide-react';

interface SleepShadeProps {
  sleep: SleepTimes;
  /** Pixel offset of a minute of the day in the grid. */
  top: (minute: number) => number;
  /** Total height of the grid (px). */
  height: number;
  /** Narrow columns: icons only next to the time. */
  compact?: boolean;
}

const shade = 'color-mix(in srgb, var(--accent) 4%, var(--surface-2))';

/**
 * Night in a time grid: shaded before getting up and after going to bed, with the two times.
 * Bedtime after midnight shades nothing in the evening (it belongs to the next morning).
 */
export function SleepShade({ sleep, top, height, compact = false }: SleepShadeProps) {
  const wake = timeToMinutes(sleep.wake);
  const bed = timeToMinutes(sleep.bed);
  const wakeY = Math.max(0, top(wake));
  const bedY = bed > wake ? top(bed) : null;
  const label = `pointer-events-none absolute flex items-center gap-1 font-semibold text-muted tabular-nums ${
    compact ? 'right-1 text-[10px]' : 'right-2 text-[11px]'
  }`;
  return (
    <>
      {wakeY > 0 && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 border-b border-dashed border-line"
          style={{ height: wakeY, background: shade }}
        />
      )}
      {wakeY > 14 && (
        <span aria-hidden className={label} style={{ top: wakeY - 15 }}>
          <Sun size={compact ? 10 : 12} />
          {formatTimeSl(sleep.wake)}
        </span>
      )}
      {bedY !== null && bedY < height && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 border-t border-dashed border-line"
            style={{ top: bedY, height: height - bedY, background: shade }}
          />
          <span aria-hidden className={label} style={{ top: bedY + 3 }}>
            <Moon size={compact ? 10 : 12} />
            {formatTimeSl(sleep.bed)}
          </span>
        </>
      )}
    </>
  );
}
